import {useLocation} from '@github-ui/react-router'
import {useMemo} from 'react'

import type {ViewConfiguration, ViewConfigurationOverrides} from '../../contexts/views/view-configuration'
import {readVisibleFieldsOverride} from '../../utils/saved-view-url-params'
import {Capability, type FieldsData} from './fields'
import {getFieldsForCapability} from './get-fields-for-capability'
import {useSavedViewUrlActions} from './use-saved-view-url-actions'

interface UseSavedViewUrlConfigurationInput {
  /** The configuration the server rendered against, which already includes axis overrides. */
  applied: ViewConfiguration
  /** Saved settings, or the host's defaults when nothing is saved. URL params are written relative to it. */
  baseline: ViewConfiguration
  /** Once loaded, `visible_fields` entries the view does not offer as attributes are ignored. */
  fields?: FieldsData
}

/**
 * Reads and writes a saved view's configuration in the URL. Returns the props a host passes to
 * `SavedViewConfigurationProvider`.
 *
 * Axis overrides (`layout`, `group_by`, `swimlane_by`) reach the server, so `applied` already
 * reflects them. Visible fields stay client-side (outside the route's query key, so toggling
 * one does not refetch) and are read from the URL here.
 */
export function useSavedViewUrlConfiguration({applied, baseline, fields}: UseSavedViewUrlConfigurationInput): {
  appliedConfiguration: ViewConfiguration
  onConfigurationChange: (changes: ViewConfigurationOverrides) => void
  onReset: () => void
} {
  const {search} = useLocation()

  const knownFields = useMemo(
    () =>
      fields
        ? new Set(getFieldsForCapability(fields, Capability.ATTRIBUTABLE).map(field => field.identifier))
        : undefined,
    [fields],
  )
  const urlVisibleFields = useMemo(
    () => readVisibleFieldsOverride(new URLSearchParams(search), applied.layout, knownFields),
    [applied.layout, knownFields, search],
  )
  const appliedConfiguration = useMemo(
    () => (urlVisibleFields ? {...applied, visibleFields: urlVisibleFields} : applied),
    [applied, urlVisibleFields],
  )

  const {applyUrlChanges, clearOverrides} = useSavedViewUrlActions({baseline, current: applied})

  return {appliedConfiguration, onConfigurationChange: applyUrlChanges, onReset: clearOverrides}
}
